import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { EventsFilterApiService } from '../../../../services/events-filter/events-filter-api.service';
import { FilterMetadata, canShowGroupCalendarEventsFilter } from '../../../../services/events-filter/events-filter.model';
import { dashboardFiltersToCriteria } from '../../../../services/dashboard/dashboard-events-filter.mapper';
import { FilterMultiSelectComponent } from '../../duna-events-filter/filter-multi-select.component';
import { EVENT_TYPE_FILTER_LABELS } from '../../../../constants/event-types';
import { EventsFilterCriteria } from '../../../../services/events-filter/events-filter.model';

@Component({
  selector: 'app-dashboard-events-calendar-filter-panel',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCheckboxModule,
    MatIconModule,
    FilterMultiSelectComponent,
  ],
  templateUrl: './dashboard-events-calendar-filter-panel.component.html',
  styleUrl: './dashboard-events-calendar-filter-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardEventsCalendarFilterPanelComponent implements OnInit {
  @Input() filters: Record<string, unknown> = {};
  @Output() filtersChange = new EventEmitter<Record<string, unknown>>();

  metadata: FilterMetadata | null = null;
  readonly eventTypeLabels = EVENT_TYPE_FILTER_LABELS;

  constructor(
    private api: EventsFilterApiService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.api.getFilterMetadata().subscribe((m) => {
      this.metadata = m;
      this.cdr.markForCheck();
    });
  }

  get criteria(): EventsFilterCriteria {
    return dashboardFiltersToCriteria(this.filters);
  }

  get showGroupCalendarFilter(): boolean {
    return canShowGroupCalendarEventsFilter(this.metadata, this.criteria);
  }

  get showOrganizationFilter(): boolean {
    return (this.metadata?.organizations?.length ?? 0) > 0;
  }

  get organizationIds(): number[] {
    const v = this.filters['organizationIds'];
    return Array.isArray(v) ? v.map((x) => Number(x)).filter((n) => Number.isFinite(n)) : [];
  }

  get educationalPathIds(): number[] {
    const v = this.filters['educationalPathIds'];
    return Array.isArray(v) ? v.map((x) => Number(x)).filter((n) => Number.isFinite(n)) : [];
  }

  get moduleIds(): number[] {
    const v = this.filters['moduleIds'];
    return Array.isArray(v) ? v.map((x) => Number(x)).filter((n) => Number.isFinite(n)) : [];
  }

  get locationGuids(): string[] {
    const v = this.filters['locationGuids'];
    return Array.isArray(v) ? v.map((x) => String(x)).filter(Boolean) : [];
  }

  get eventTypes(): string[] {
    return (this.filters['eventTypes'] as string[]) || this.metadata?.defaults?.eventTypes || [
      'education',
      'course',
      'activity',
    ];
  }

  patch(partial: Record<string, unknown>): void {
    this.filtersChange.emit({ ...this.filters, ...partial });
  }

  toggleEventType(slug: string): void {
    const set = new Set(this.eventTypes);
    if (set.has(slug)) set.delete(slug);
    else set.add(slug);
    this.patch({ eventTypes: Array.from(set) });
  }

  isEventTypeOn(slug: string): boolean {
    return this.eventTypes.includes(slug);
  }

  onOrgChange(ids: number[]): void {
    this.patch({ organizationIds: ids });
  }

  onPathsChange(ids: number[]): void {
    this.patch({ educationalPathIds: ids, educationalPathVersionIds: [] });
  }

  onModulesChange(ids: number[]): void {
    this.patch({ moduleIds: ids });
  }

  onLocationsChange(guids: string[]): void {
    this.patch({ locationGuids: guids });
  }

  orgOptions() {
    return (this.metadata?.organizations || []).map((o) => ({
      value: o.id,
      label: o.full_name || o.org_name,
    }));
  }

  pathOptions() {
    return (this.metadata?.educationalPaths || []).map((p) => ({
      value: p.path_id,
      label: p.full_name || p.name,
    }));
  }

  moduleOptions() {
    return (this.metadata?.modules || []).map((m) => ({
      value: m.module_id,
      label: m.name,
    }));
  }

  locationOptions() {
    return (this.metadata?.locations || []).map((l) => ({
      value: l.guid,
      label: l.locations_name,
    }));
  }

  formatOrgs(): string {
    const ids = (this.filters['organizationIds'] as number[]) || [];
    return (this.metadata?.organizations || [])
      .filter((o) => ids.includes(o.id))
      .map((o) => o.full_name || o.org_name)
      .join(', ');
  }

  formatPaths(): string {
    const ids = (this.filters['educationalPathIds'] as number[]) || [];
    return (this.metadata?.educationalPaths || [])
      .filter((p) => ids.includes(p.path_id))
      .map((p) => p.full_name || p.name)
      .join(', ');
  }

  formatModules(): string {
    const ids = (this.filters['moduleIds'] as number[]) || [];
    return (this.metadata?.modules || [])
      .filter((m) => ids.includes(m.module_id))
      .map((m) => m.name)
      .join(', ');
  }

  formatLocations(): string {
    const guids = (this.filters['locationGuids'] as string[]) || [];
    return (this.metadata?.locations || [])
      .filter((l) => guids.includes(l.guid))
      .map((l) => l.locations_name)
      .join(', ');
  }
}
